import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * The portal's Files table: 14px header cells, 55px rows, a hairline under each
 * row and a light blue hover.
 */
/**
 * True only once something has been busy for a moment, so a read that answers quickly says
 * nothing at all. What is said is said to assistive technology; the one thing drawn for a
 * sighted reader is the hairline across the top of the page, which every request raises.
 */
function useSettledBusy(busy: boolean | undefined, delay = 320): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!busy) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), delay);
    return () => window.clearTimeout(timer);
  }, [busy, delay]);
  return slow;
}

export function TableWrap({
  className,
  busy,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  /** True while the rows on screen are being replaced by a newer page. */
  busy?: boolean;
}) {
  const working = useSettledBusy(busy);
  // The rows used to dim to 72% under a tint that swallowed clicks whenever the list was
  // re-read. Lists re-read themselves on a timer, so the page faded and came back on its own
  // every few seconds, which reads as a fault rather than as work - and the rows it greyed
  // out were the rows it was about to put back unchanged.
  return <div aria-busy={working || undefined} className={cn('w-full overflow-x-auto', className)} {...props} />;
}

export function Table({ className, ...props }: React.TableHTMLAttributes<HTMLTableElement>) {
  return <table className={cn('w-full border-collapse text-base', className)} {...props} />;
}

export function THead({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      // The soft shadow under the head is the portal's, and it is what makes the head read as
      // a lid on the rows rather than as a first row with bold text.
      style={{ boxShadow: '0 2px 10px 0 rgb(0 0 0 / 0.06)' }}
      className={cn(
        'bg-[color:var(--ts-thead-bg)]',
        '[&_th]:border-b [&_th]:border-[color:var(--ts-border-soft)]',
        className,
      )}
      {...props}
    />
  );
}

export function TH({
  className,
  align = 'left',
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn(
        'h-12 px-4 text-base font-medium whitespace-nowrap text-gray-2',
        'first:rounded-tl-lg last:rounded-tr-lg',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
      {...props}
    />
  );
}

export function TBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('bg-surface', className)} {...props} />;
}

export function TR({
  className,
  interactive = false,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        'h-[55px] border-b border-[color:var(--ts-row-border)] transition-colors duration-150 ease-out last:border-b-0',
        interactive &&
          'cursor-pointer hover:bg-[color:var(--ts-row-hover)] focus-visible:bg-[color:var(--ts-row-hover)] ' +
            'focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)]',
        className,
      )}
      {...props}
    />
  );
}

export function TD({
  className,
  numeric = false,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn('px-4 align-middle text-base text-gray-3', numeric && 'text-right tabular-nums', className)}
      {...props}
    />
  );
}

/** Search on the left, selects and the primary action on the right. */
/**
 * A labelled control in a table's toolbar.
 *
 * The portal names its controls above them - "Search", "File Type" - rather than leaving the
 * placeholder to do it. A placeholder disappears the moment somebody types, so a filled-in
 * filter stops saying what it filters.
 */
export function ToolbarField({
  label,
  htmlFor,
  className,
  children,
}: {
  label: string;
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-gray-2">
        {label}
      </label>
      {children}
    </div>
  );
}

export function TableToolbar({
  search,
  searchLabel = 'Search',
  searchFor,
  children,
  className,
}: {
  search?: React.ReactNode;
  /** The name above the search box. Null leaves it unlabelled. */
  searchLabel?: string | null;
  /** id of the input the label belongs to. */
  searchFor?: string;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-end gap-3 pb-5', className)}>
      {search ? (
        searchLabel ? (
          <ToolbarField label={searchLabel} htmlFor={searchFor} className="w-full max-w-[380px] min-w-[200px]">
            {search}
          </ToolbarField>
        ) : (
          <div className="w-full max-w-[380px] min-w-[200px]">{search}</div>
        )
      ) : null}
      {/*
        Buttons in a toolbar stand beside filters, so they take the height of a filter (40px)
        rather than the 36px they take in a form. Sizing them here rather than at each call
        site keeps every toolbar in the app on one line: the mismatch was four pixels, which
        is small enough to miss when writing a page and impossible to miss on the page.
      */}
      {children ? (
        <div className="ml-auto flex flex-wrap items-end gap-3 [&>a]:h-10 [&>button]:h-10">{children}</div>
      ) : null}
    </div>
  );
}

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

/** Column header that toggles sorting, with the portal's small sort glyph. */
export function SortableTH({
  columnKey,
  sort,
  onSort,
  align = 'left',
  children,
  className,
}: {
  columnKey: string;
  sort: SortState | null;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
}) {
  const active = sort?.key === columnKey;
  const Glyph = !active ? ChevronsUpDown : sort?.direction === 'asc' ? ChevronUp : ChevronDown;
  return (
    <TH
      align={align}
      className={cn('p-0', className)}
      aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        aria-label={`Sort by ${typeof children === 'string' ? children : columnKey}`}
        className={cn(
          'flex h-12 w-full items-center gap-1.5 px-4 font-medium transition-colors duration-150 ease-out',
          'hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
          'focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--ts-focus-ring)]',
          align === 'right' ? 'justify-end' : 'justify-start',
          active ? 'text-primary' : 'text-gray-2',
        )}
      >
        {children}
        <Glyph
          className={cn('h-3.5 w-3.5 shrink-0', active ? 'text-primary opacity-100' : 'opacity-60')}
          aria-hidden="true"
        />
      </button>
    </TH>
  );
}

/**
 * Sort state a table hands to the server, as `created` or `records:asc`.
 *
 * The client-side twin below orders the rows it was given, which is the right answer only
 * while a table holds its whole list. Once a table shows one page of many, ordering those
 * rows orders the page rather than the list, and the answer changes with whichever page
 * somebody is looking at. So the column headers set this and the query carries it.
 */
export function useServerSort(initial: SortState | null = null, onChange?: () => void) {
  const [sort, setSort] = useState<SortState | null>(initial);

  const onSort = (key: string) => {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 'asc' };
      // Third click clears it: a column can be read up, down, or left alone.
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
    // A different order is a different list, so page one is where it starts.
    onChange?.();
  };

  const param = sort ? `${sort.key}:${sort.direction}` : undefined;
  return { sort, onSort, param };
}

/** Sort state plus the sorted rows, using one accessor per column key. */
export function useTableSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => string | number | null | undefined>,
  initial: SortState | null = null,
) {
  const [sort, setSort] = useState<SortState | null>(initial);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const read = accessors[sort.key];
    if (!read) return rows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((left, right) => {
      const a = read(left);
      const b = read(right);
      if (a === b) return 0;
      if (a === null || a === undefined) return 1;
      if (b === null || b === undefined) return -1;
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * factor;
      return String(a).localeCompare(String(b), 'en', { numeric: true }) * factor;
    });
    // accessors is a literal per render; the keys are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  function onSort(key: string) {
    setSort((current) => {
      if (current?.key !== key) return { key, direction: 'asc' };
      return current.direction === 'asc' ? { key, direction: 'desc' } : null;
    });
  }

  return { sort, onSort, sorted };
}

/**
 * Page numbers with an ellipsis, on the portal's rule: every page up to seven, and after that
 * the first, two either side of the one you are on, and the last.
 *
 * Two either side rather than one. A window of three numbers around the current page makes
 * every click a leap of faith; five is enough to see where you are going.
 */
function pageList(current: number, total: number): (number | 'gap')[] {
  const SIDE_BUFFER = 2;
  if (total <= 3 + 2 * SIDE_BUFFER) return Array.from({ length: total }, (_, index) => index + 1);

  const pages = new Set<number>([1, total]);
  for (let page = current - SIDE_BUFFER; page <= current + SIDE_BUFFER; page += 1) pages.add(page);
  const kept = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);

  const out: (number | 'gap')[] = [];
  let previous = 0;
  for (const page of kept) {
    if (previous && page - previous > 1) out.push('gap');
    out.push(page);
    previous = page;
  }
  return out;
}

/** "Showing 1-10 of 98 results" on the left, page numbers on the right. */
export function TablePagination({
  page,
  pageSize,
  total,
  hasMore = false,
  busy = false,
  onPageChange,
  className,
}: {
  page: number;
  pageSize: number;
  /** Rows known so far. With `hasMore`, Tab Service has more pages than this. */
  total: number;
  /** True while Tab Service still has a page beyond the rows counted in `total`. */
  hasMore?: boolean;
  /** True while the page being shown is still being read. */
  busy?: boolean;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  // Page buttons stay live while a read is in flight: a background re-read of the list is
  // not a reason for the reader to be unable to turn the page.
  const working = useSettledBusy(busy);
  const totalPages = Math.max(1, Math.ceil(total / pageSize), hasMore ? page + 1 : 1);
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  // Nothing to say when everything is already on screen: "Showing 1-3 of 3 results" is a line
  // of furniture under a table somebody can see the whole of. The portal leaves it out too.
  const worthCounting = hasMore || total > pageSize;

  return (
    <div
      aria-busy={working || undefined}
      className={cn(
        'flex flex-wrap items-center gap-3 pt-5',
        worthCounting ? 'justify-between' : 'justify-end',
        className,
      )}
    >
      {worthCounting ? (
      <p className="text-base text-gray-3">
        Showing <span className="tabular-nums">{first}</span>-<span className="tabular-nums">{last}</span> of{' '}
        <span className="tabular-nums">
          {total}
          {hasMore ? '+' : ''}
        </span>{' '}
        {total === 1 && !hasMore ? 'result' : 'results'}
      </p>
      ) : null}
      {totalPages > 1 ? (
        <nav aria-label="Pagination" className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-gray-1 transition-colors duration-150 ease-out',
              'enabled:hover:bg-[color:var(--ts-row-hover)] enabled:hover:text-primary',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            <ChevronLeftGlyph />
          </button>
          {pageList(page, totalPages).map((entry, index) =>
            entry === 'gap' ? (
              <span key={`gap-${index}`} className="px-1.5 text-gray-1">
                ...
              </span>
            ) : (
              <button
                key={entry}
                type="button"
                aria-current={entry === page ? 'page' : undefined}
                onClick={() => onPageChange(entry)}
                className={cn(
                  'h-8 min-w-8 rounded-md px-2 text-base transition-colors duration-150 ease-out',
                  entry === page
                    ? 'bg-primary-light font-medium text-primary'
                    : 'text-gray-3 hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
                )}
              >
                {entry}
              </button>
            ),
          )}
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-gray-1 transition-colors duration-150 ease-out',
              'enabled:hover:bg-[color:var(--ts-row-hover)] enabled:hover:text-primary',
              'disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            <ChevronRightGlyph />
          </button>
        </nav>
      ) : null}
    </div>
  );
}

function ChevronLeftGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true" fill="none">
      <path d="M10 3 5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRightGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true" fill="none">
      <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
